import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { 
  Zap, 
  ArrowLeft, 
  ArrowRight,
  Download, 
  Activity, 
  Heart,
  Flame,
  Users,
  TrendingUp,
  Calendar,
  Trash2,
  Loader2,
  History,
  ChevronDown,
  ChevronUp,
  Globe,
  Share2,
  Copy,
  Check
} from "lucide-react";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
  Legend
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ZONE_COLORS = [
  "#808080", // Zone 1 - Gray
  "#0080FF", // Zone 2 - Blue
  "#00FF80", // Zone 3 - Green
  "#D4FF00", // Zone 4 - Volt
  "#FFAA00", // Zone 5 - Orange
  "#FF0055", // Zone 6 - Red
  "#8000FF", // Zone 7 - Purple
];

export default function Analysis() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [athleteHistory, setAthleteHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareLink, setShareLink] = useState("");
  const [creatingShare, setCreatingShare] = useState(false);

  useEffect(() => {
    fetchAnalysis();
    fetchPaymentStatus();
  }, [id]);

  const fetchPaymentStatus = async () => {
    try {
      const response = await fetch(`${API}/payment/status`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const data = await response.json();
        // User has subscription if subscription_type is "subscription" and is_active
        setHasSubscription(data.subscription_type === "subscription" && data.is_active);
      }
    } catch (error) {
      console.error("Error fetching payment status:", error);
    }
  };

  // Fetch athlete history when analysis is loaded
  useEffect(() => {
    if (analysis?.athlete_name && analysis.athlete_name !== "Unbenannt") {
      fetchAthleteHistory(analysis.athlete_name);
    }
  }, [analysis?.athlete_name]);

  const fetchAthleteHistory = async (athleteName) => {
    try {
      const response = await fetch(`${API}/athlete/${encodeURIComponent(athleteName)}/history`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (response.ok) {
        const data = await response.json();
        setAthleteHistory(data);
      }
    } catch (error) {
      console.error("Error fetching athlete history:", error);
    }
  };

  const fetchAnalysis = async () => {
    try {
      const response = await fetch(`${API}/analysis/${id}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (response.ok) {
        const data = await response.json();
        setAnalysis(data);
      } else {
        toast.error(t('analysis.notFound'));
      }
    } catch (error) {
      console.error("Error fetching analysis:", error);
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const deleteAnalysis = async () => {
    setDeleting(true);
    try {
      const response = await fetch(`${API}/analysis/${id}`, {
        method: "DELETE",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (response.ok) {
        toast.success(t('success.delete'));
        navigate("/dashboard");
      } else {
        const error = await response.json();
        toast.error(error.detail || t('errors.generic'));
      }
    } catch (error) {
      console.error("Error deleting analysis:", error);
      toast.error(t('errors.generic'));
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const downloadPDF = async (lang = "de") => {
    setDownloading(true);
    
    // Retry logic for network issues
    const maxRetries = 2;
    let lastError = null;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(`${API}/analysis/${id}/pdf?lang=${lang}`, {
          method: "GET",
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          }
        });

        if (response.ok) {
          const blob = await response.blob();
          
          // Verify blob is valid
          if (blob.size === 0) {
            throw new Error("Empty PDF received");
          }
          
          // Create object URL
          const blobUrl = window.URL.createObjectURL(blob);
          
          // Create and configure link
          const link = document.createElement('a');
          link.style.display = 'none';
          link.href = blobUrl;
          link.download = `velometrics_report_${id}_${lang}.pdf`;
          
          // Append to body
          document.body.appendChild(link);
          
          // Trigger click
          link.click();
          
          // Cleanup after download starts
          setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
          }, 1000);
          
          const successMsg = lang === "de" ? "PDF heruntergeladen!" : "PDF downloaded!";
          toast.success(successMsg);
          setDownloading(false);
          return; // Success - exit function
        } else {
          const errorText = await response.text();
          console.error("PDF download failed:", response.status, errorText);
          throw new Error(`Server error: ${response.status}`);
        }
      } catch (error) {
        lastError = error;
        console.error(`PDF download attempt ${attempt + 1} failed:`, error);
        
        // If it's a network error and we have retries left, wait and retry
        if ((error.message === 'Failed to fetch' || error.message.includes('network')) && attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 500));
          continue;
        }
        
        break;
      }
    }
    
    // Show error only after all retries failed
    console.error("PDF download error after retries:", lastError);
    toast.error("PDF Download fehlgeschlagen. Bitte versuchen Sie es erneut.");
    setDownloading(false);
  };

  const createShareLink = async () => {
    setCreatingShare(true);
    try {
      const response = await fetch(`${API}/analysis/${id}/share`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ expires_days: 30 })
      });

      if (response.ok) {
        const data = await response.json();
        setShareLink(data.share_url);
        setShowShareDialog(true);
        toast.success(t('analysis.share.linkCreated'));
      } else {
        const error = await response.json();
        throw new Error(error.detail || t('analysis.share.createError'));
      }
    } catch (error) {
      console.error("Share error:", error);
      toast.error(error.message || t('analysis.share.createError'));
    } finally {
      setCreatingShare(false);
    }
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      toast.success(t('success.copied'));
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = shareLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      toast.success(t('success.copied'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-volt"></div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-display text-2xl font-bold mb-4">{t('analysis.notFound')}</h2>
          <Link to="/dashboard">
            <Button>{t('analysis.back')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Prepare chart data - combine power and HR data
  const powerDataRaw = analysis.raw_data?.power_data || [];
  const hrDataRaw = analysis.raw_data?.hr_data || [];
  
  // Downsample data for performance (max 500 points)
  const sampleRate = Math.max(1, Math.floor(powerDataRaw.length / 500));
  
  const testData = powerDataRaw
    .filter((_, index) => index % sampleRate === 0)
    .map((power, index) => {
      const originalIndex = index * sampleRate;
      const minutes = Math.floor(originalIndex / 60);
      const seconds = originalIndex % 60;
      return {
        time: originalIndex,
        timeLabel: `${minutes}:${seconds.toString().padStart(2, '0')}`,
        power: power,
        hr: hrDataRaw[originalIndex] || null
      };
    });

  // Guard against null values for HR-only analyses
  const zoneEntries = analysis.power_zones ? Object.entries(analysis.power_zones) : [];
  const zoneData = zoneEntries.map(([key, zone], index) => {
    // Für Zone 7 (Neuromuskulär): max = vorherige Zone max + 10
    let displayMax = zone.max;
    let isOpenEnded = false;
    
    if (index === zoneEntries.length - 1 && zone.max > 1000) {
      // Letzte Zone mit sehr hohem max-Wert -> offenes Ende
      const prevZone = zoneEntries[index - 1]?.[1];
      displayMax = prevZone ? prevZone.max + 10 : zone.min + 10;
      isOpenEnded = true;
    }
    
    return {
      name: zone.name,
      min: zone.min,
      max: displayMax,
      originalMax: zone.max,
      isOpenEnded: isOpenEnded,
      rangeText: isOpenEnded ? `> ${zone.min} W` : `${zone.min} - ${zone.max} W`,
      color: ZONE_COLORS[index]
    };
  });

  const carbData = analysis.carb_consumption 
    ? Object.entries(analysis.carb_consumption)
        .filter(([watt]) => !watt.startsWith('_'))  // Filter out internal keys like _fatmax
        .map(([watt, data]) => ({
          watt: watt,
          carbs: data.carbs_g_per_hour,
          description: data.description
        }))
    : [];
  
  // Calculate max values for chart scaling (guard against null ftp)
  const maxPower = analysis.ftp 
    ? Math.max(...powerDataRaw, analysis.ftp * 1.3) 
    : (powerDataRaw.length > 0 ? Math.max(...powerDataRaw) : 100);
  const maxHR = analysis.raw_data?.max_hr || 200;

  // Helper function to format date without timezone shift
  const formatDateLocal = (dateString, options = {}) => {
    if (!dateString) return "";
    const parts = dateString.split("-");
    if (parts.length === 3) {
      const year = parseInt(parts[0]);
      const month = parseInt(parts[1]) - 1;
      const day = parseInt(parts[2]);
      const date = new Date(year, month, day);
      const locale = i18n.language === 'de' ? "de-DE" : "en-US";
      return date.toLocaleDateString(locale, options);
    }
    const locale = i18n.language === 'de' ? "de-DE" : "en-US";
    return new Date(dateString).toLocaleDateString(locale, options);
  };

  const getPeerBadgeClass = (category) => {
    const classes = {
      "Exzellent": "excellent",
      "Elite": "excellent",
      "Gut": "good",
      "Fortgeschritten": "good",
      "Advanced": "good",
      "Durchschnittlich": "average",
      "Mittelfeld": "average",
      "Intermediate": "average",
      "Unter Durchschnitt": "below",
      "Entwicklung": "below",
      "Developing": "below",
      "Verbesserungswürdig": "below",
      "Anfänger": "below",
      "Beginner": "below"
    };
    return classes[category] || "average";
  };

  // Helper function to get peer comparison data (handles both old and new format)
  const getPeerData = () => {
    const peer = analysis.peer_comparison;
    // Return null for HR-only analyses (no peer comparison available)
    if (!peer) return null;
    
    if (peer.overall_category_de) {
      // New extended format
      return {
        category: peer.overall_category_de,
        percentile: peer.overall_percentile,
        age_group: peer.age_group,
        gender: peer.gender,
        training_level: peer.training_level_de,
        vo2max: peer.vo2max_comparison?.your_vo2max || analysis.vo2max,
        ftp_per_kg: peer.ftp_comparison?.ftp_per_kg || analysis.ftp_per_kg,
        description: peer.vo2max_comparison?.description || '',
        bar_chart_data: peer.bar_chart_data || [],
        vo2max_percentile: peer.vo2max_comparison?.percentile || peer.overall_percentile,
        ftp_percentile: peer.ftp_comparison?.percentile || peer.overall_percentile,
        vo2max_category: peer.vo2max_comparison?.category || peer.overall_category_de,
        ftp_category: peer.ftp_comparison?.category_de || peer.overall_category_de,
        isExtended: true
      };
    } else {
      // Old format
      return {
        category: peer.category,
        percentile: peer.percentile,
        age_group: peer.age_group,
        vo2max: peer.your_vo2max,
        description: peer.description,
        isExtended: false
      };
    }
  };

  const peerData = getPeerData();

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16 gap-2">
            {/* Back Link */}
            <Link to="/dashboard" className="flex items-center gap-1 text-muted-foreground hover:text-white flex-shrink-0">
              <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="text-xs sm:text-sm font-medium hidden xs:inline">{t('analysis.back')}</span>
            </Link>
            
            {/* Action Buttons - Always visible */}
            <div className="flex items-center gap-1 flex-shrink-0">
              {/* Language Switcher - Hidden on very small screens */}
              <div className="hidden sm:block">
                <LanguageSwitcher />
              </div>
              
              {/* PDF Download */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    disabled={downloading}
                    size="sm"
                    className="bg-volt text-black hover:bg-volt/90 font-bold text-xs h-8 px-2 sm:px-3"
                    data-testid="download-pdf-btn"
                  >
                    {downloading ? (
                      <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 animate-spin" />
                    ) : (
                      <Download className="h-3 w-3 sm:h-4 sm:w-4" />
                    )}
                    <span className="hidden sm:inline ml-1">PDF</span>
                    <ChevronDown className="h-3 w-3 ml-0.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-700">
                  <DropdownMenuItem 
                    onClick={() => downloadPDF("de")}
                    className="cursor-pointer hover:bg-zinc-800 text-white"
                    data-testid="download-pdf-de"
                  >
                    <Globe className="h-4 w-4 mr-2" />
                    🇩🇪 Deutsch
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => downloadPDF("en")}
                    className="cursor-pointer hover:bg-zinc-800 text-white"
                    data-testid="download-pdf-en"
                  >
                    <Globe className="h-4 w-4 mr-2" />
                    🇬🇧 English
                  </DropdownMenuItem>
                  {/* Mobile Language Switch */}
                  <div className="sm:hidden border-t border-zinc-700 mt-1 pt-1">
                    <DropdownMenuItem 
                      onClick={() => i18n.changeLanguage(i18n.language === 'de' ? 'en' : 'de')}
                      className="cursor-pointer hover:bg-zinc-800 text-white"
                    >
                      <Globe className="h-4 w-4 mr-2" />
                      {i18n.language === 'de' ? '🇬🇧 Switch to English' : '🇩🇪 Zu Deutsch wechseln'}
                    </DropdownMenuItem>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
              
              {/* Share Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={createShareLink}
                disabled={creatingShare}
                className="border-cyan/30 text-cyan hover:bg-cyan/10 h-8 px-2"
                data-testid="share-analysis-btn"
              >
                {creatingShare ? (
                  <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 animate-spin" />
                ) : (
                  <Share2 className="h-3 w-3 sm:h-4 sm:w-4" />
                )}
              </Button>
              
              {/* Delete Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDeleteDialog(true)}
                className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 h-8 px-2"
                data-testid="delete-analysis-btn"
              >
                <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 grid-bg" data-testid="analysis-page">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="data-label mb-2 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {/* Show test date if available, otherwise created_at */}
                {analysis.athlete_data?.test_date ? (
                  <>
                    {formatDateLocal(analysis.athlete_data.test_date, {
                      day: "2-digit",
                      month: "long",
                      year: "numeric"
                    })}
                    {analysis.athlete_data?.test_time && (
                      <span className="text-muted-foreground ml-2">
                        {i18n.language === 'de' ? 'um' : 'at'} {analysis.athlete_data.test_time} {i18n.language === 'de' ? 'Uhr' : ''}
                      </span>
                    )}
                  </>
                ) : (
                  new Date(analysis.created_at).toLocaleDateString(i18n.language === 'de' ? "de-DE" : "en-US", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric"
                  })
                )}
              </p>
              <h1 className="font-display text-3xl font-bold tracking-tight uppercase">
                {analysis.athlete_name && analysis.athlete_name !== "Unbenannt" && (
                  <span className="text-volt">{analysis.athlete_name} - </span>
                )}
                {analysis.is_hr_only 
                  ? (i18n.language === 'de' ? 'HF-Analyse' : 'HR Analysis')
                  : (analysis.test_type === "20min" ? t('analysis.test20min') : t('analysis.testRamp'))
                }
              </h1>
            </div>
            {analysis.peer_comparison && (
              <div className={`peer-badge ${getPeerBadgeClass(analysis.peer_comparison.category)}`}>
                {analysis.peer_comparison.category}
              </div>
            )}
          </div>

          {/* HR-Only Notice */}
          {analysis.is_hr_only && (
            <div className="glass-card rounded-xl p-4 sm:p-6 mb-6 sm:mb-8 border border-cyan/30 bg-cyan/5">
              <div className="flex items-start gap-3">
                <Heart className="h-5 w-5 text-cyan mt-0.5" />
                <div>
                  <h3 className="font-semibold text-cyan mb-1">
                    {i18n.language === 'de' ? 'Nur Herzfrequenz-Daten' : 'Heart Rate Data Only'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {i18n.language === 'de' 
                      ? 'Diese Analyse basiert nur auf Herzfrequenzdaten. Für FTP, VO2max und Leistungszonen wird ein Powermeter benötigt.'
                      : 'This analysis is based on heart rate data only. A power meter is required for FTP, VO2max and power zones.'
                    }
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Key Metrics - Show different layout for HR-only */}
          {analysis.is_hr_only ? (
            /* HR-Only Metrics */
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
              <div className="glass-card rounded-xl p-4 sm:p-6 stat-card" data-testid="max-hr-card">
                <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
                  <Heart className="h-4 w-4 sm:h-5 sm:w-5 text-heart-red" />
                  <span className="data-label text-xs sm:text-sm">{i18n.language === 'de' ? 'Max HF' : 'Max HR'}</span>
                </div>
                <p className="number-display text-2xl sm:text-4xl text-heart-red">{analysis.raw_data?.max_hr || '-'}<span className="text-sm sm:text-lg ml-1">bpm</span></p>
              </div>
              <div className="glass-card rounded-xl p-4 sm:p-6 stat-card" data-testid="avg-hr-card">
                <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
                  <Heart className="h-4 w-4 sm:h-5 sm:w-5 text-cyan" />
                  <span className="data-label text-xs sm:text-sm">{i18n.language === 'de' ? 'Durchschn. HF' : 'Avg HR'}</span>
                </div>
                <p className="number-display text-2xl sm:text-4xl text-cyan">{analysis.raw_data?.avg_hr ? Math.round(analysis.raw_data.avg_hr) : '-'}<span className="text-sm sm:text-lg ml-1">bpm</span></p>
              </div>
              <div className="glass-card rounded-xl p-4 sm:p-6 stat-card" data-testid="bmi-card">
                <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
                  <Activity className="h-4 w-4 sm:h-5 sm:w-5 text-volt" />
                  <span className="data-label text-xs sm:text-sm">BMI</span>
                </div>
                <p className="number-display text-2xl sm:text-4xl text-volt">{analysis.bmi ? Number(analysis.bmi).toFixed(1) : '-'}</p>
              </div>
            </div>
          ) : (
            /* Full Metrics with Power */
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
            <div className="glass-card rounded-xl p-4 sm:p-6 stat-card" data-testid="ftp-card">
              <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
                <Activity className="h-4 w-4 sm:h-5 sm:w-5 text-volt" />
                <span className="data-label text-xs sm:text-sm">FTP</span>
              </div>
              <p className="number-display text-2xl sm:text-4xl text-volt">{Number(analysis.ftp).toFixed(0)}<span className="text-sm sm:text-lg ml-1">W</span></p>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">{Number(analysis.ftp_per_kg).toFixed(2)} W/kg</p>
            </div>
            <div className="glass-card rounded-xl p-4 sm:p-6 stat-card" data-testid="vo2max-card">
              <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
                <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-cyan" />
                <span className="data-label text-xs sm:text-sm">VO2max</span>
              </div>
              <p className="number-display text-2xl sm:text-4xl text-cyan">{Number(analysis.vo2max).toFixed(1)}<span className="text-xs sm:text-lg ml-1 hidden sm:inline">ml/kg/min</span></p>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">{Number(analysis.vo2max_absolute).toFixed(1)} L/min</p>
            </div>
            <div className="glass-card rounded-xl p-4 sm:p-6 stat-card" data-testid="vt1-card">
              <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
                <Heart className="h-4 w-4 sm:h-5 sm:w-5 text-carb-orange" />
                <span className="data-label text-xs sm:text-sm">VT1</span>
              </div>
              <p className="number-display text-2xl sm:text-4xl text-carb-orange">{Number(analysis.vt1).toFixed(0)}<span className="text-sm sm:text-lg ml-1">W</span></p>
            </div>
            <div className="glass-card rounded-xl p-4 sm:p-6 stat-card" data-testid="vt2-card">
              <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
                <Flame className="h-4 w-4 sm:h-5 sm:w-5 text-heart-red" />
                <span className="data-label text-xs sm:text-sm">VT2</span>
              </div>
              <p className="number-display text-2xl sm:text-4xl text-heart-red">{Number(analysis.vt2).toFixed(0)}<span className="text-sm sm:text-lg ml-1">W</span></p>
            </div>
          </div>
          )}

          {/* BMI Card */}
          {analysis.bmi && !analysis.is_hr_only && (
            <div className="glass-card rounded-xl p-4 sm:p-6 mb-6 sm:mb-8">
              <h3 className="font-display font-bold uppercase mb-3 sm:mb-4 text-sm sm:text-base">{t('analysis.bodyData')}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-6">
                <div>
                  <p className="data-label text-xs">{t('analysis.bmi')}</p>
                  <p className="number-display text-lg sm:text-2xl">{Number(analysis.bmi.value).toFixed(1)}</p>
                  <p className="text-xs text-muted-foreground">{analysis.bmi.category}</p>
                </div>
                <div>
                  <p className="data-label text-xs">{t('analysis.weight')}</p>
                  <p className="number-display text-lg sm:text-2xl">{Number(analysis.athlete_data?.weight_kg).toFixed(1)} <span className="text-xs sm:text-sm">kg</span></p>
                </div>
                <div>
                  <p className="data-label text-xs">{t('analysis.height')}</p>
                  <p className="number-display text-lg sm:text-2xl">{Number(analysis.athlete_data?.height_cm).toFixed(0)} <span className="text-xs sm:text-sm">cm</span></p>
                </div>
                <div>
                  <p className="data-label text-xs">{t('analysis.age')}</p>
                  <p className="number-display text-lg sm:text-2xl">{analysis.athlete_data?.age} <span className="text-xs sm:text-sm">{t('analysis.years')}</span></p>
                </div>
                {analysis.laktat?.max_value && (
                  <div>
                    <p className="data-label text-xs">{t('analysis.laktatMax')}</p>
                    <p className="number-display text-lg sm:text-2xl text-purple-400">{Number(analysis.laktat.max_value).toFixed(1)} <span className="text-xs sm:text-sm">mmol/L</span></p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Laktat Chart - Hide for HR-only */}
          {!analysis.is_hr_only && analysis.laktat?.measurements && analysis.laktat.measurements.length > 0 && (
            <div className="glass-card rounded-xl p-6 mb-8">
              <h3 className="font-display font-bold uppercase mb-2">{t('analysis.laktatCurve')}</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {t('analysis.laktatSubtitle')}
              </p>
              
              {/* VLaMax und Schwellen-Info */}
              {analysis.laktat.analysis && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  {analysis.laktat.analysis.vlamax && (
                    <div className="bg-zinc-900/50 rounded-lg p-3 border border-purple-500/20">
                      <p className="text-xs text-muted-foreground">VLaMax</p>
                      <p className="number-display text-lg text-purple-400">
                        {Number(analysis.laktat.analysis.vlamax).toFixed(2)}
                        <span className="text-xs ml-1">mmol/l/s</span>
                      </p>
                      {analysis.laktat.analysis.vlamax_category && (
                        <p className="text-xs text-purple-300 mt-1">{analysis.laktat.analysis.vlamax_category}</p>
                      )}
                    </div>
                  )}
                  {analysis.laktat.analysis.vt1_lactate && (
                    <div className="bg-zinc-900/50 rounded-lg p-3 border border-carb-orange/20">
                      <p className="text-xs text-muted-foreground">{t('analysis.laktatAtVt1')}</p>
                      <p className="number-display text-lg text-carb-orange">
                        {Number(analysis.laktat.analysis.vt1_lactate).toFixed(2)}
                        <span className="text-xs ml-1">mmol/l</span>
                      </p>
                    </div>
                  )}
                  {analysis.laktat.analysis.vt2_lactate && (
                    <div className="bg-zinc-900/50 rounded-lg p-3 border border-heart-red/20">
                      <p className="text-xs text-muted-foreground">Laktat @ VT2</p>
                      <p className="number-display text-lg text-heart-red">
                        {Number(analysis.laktat.analysis.vt2_lactate).toFixed(2)}
                        <span className="text-xs ml-1">mmol/l</span>
                      </p>
                    </div>
                  )}
                  <div className="bg-zinc-900/50 rounded-lg p-3 border border-purple-500/20">
                    <p className="text-xs text-muted-foreground">Laktat max</p>
                    <p className="number-display text-lg text-purple-400">
                      {Number(analysis.laktat.max_value).toFixed(2)}
                      <span className="text-xs ml-1">mmol/l</span>
                    </p>
                  </div>
                </div>
              )}
              
              {/* Power at Lactate Thresholds */}
              {analysis.laktat.analysis?.power_at_lactate && Object.keys(analysis.laktat.analysis.power_at_lactate).length > 0 && (
                <div className="mb-6">
                  <p className="text-xs text-muted-foreground mb-2">{t('analysis.powerAtLactate')}</p>
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(analysis.laktat.analysis.power_at_lactate).map(([threshold, power]) => (
                      <div key={threshold} className="bg-zinc-800/50 px-3 py-2 rounded-lg">
                        <span className="text-purple-400 font-mono">{threshold.replace('mmol', ' mmol/l')}</span>
                        <span className="text-muted-foreground mx-2">→</span>
                        <span className="text-volt font-bold">{power}W</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={analysis.laktat.measurements}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis 
                      dataKey="watt" 
                      stroke="#666"
                      label={{ value: 'Watt', position: 'bottom', fill: '#666', fontSize: 12 }}
                    />
                    <YAxis 
                      stroke="#a855f7"
                      domain={[0, 'dataMax + 2']}
                      label={{ value: 'Laktat (mmol/L)', angle: -90, position: 'insideLeft', fill: '#a855f7', fontSize: 12 }}
                    />
                    
                    {/* VT1 und VT2 Referenzlinien */}
                    <ReferenceLine 
                      x={analysis.vt1} 
                      stroke="#FFAA00" 
                      strokeDasharray="5 5"
                      strokeWidth={2}
                      label={{ value: `VT1 (${Number(analysis.vt1).toFixed(0)}W)`, fill: '#FFAA00', fontSize: 10, position: 'top' }}
                    />
                    <ReferenceLine 
                      x={analysis.vt2} 
                      stroke="#FF0055" 
                      strokeDasharray="5 5"
                      strokeWidth={2}
                      label={{ value: `VT2 (${Number(analysis.vt2).toFixed(0)}W)`, fill: '#FF0055', fontSize: 10, position: 'top' }}
                    />
                    <ReferenceLine 
                      x={analysis.ftp} 
                      stroke="#D4FF00" 
                      strokeDasharray="5 5"
                      strokeWidth={2}
                      label={{ value: `FTP (${Number(analysis.ftp).toFixed(0)}W)`, fill: '#D4FF00', fontSize: 10, position: 'top' }}
                    />
                    
                    {/* 2 mmol und 4 mmol Referenzlinien */}
                    <ReferenceLine 
                      y={2} 
                      stroke="#666" 
                      strokeDasharray="3 3"
                      label={{ value: '2 mmol/L (LT)', fill: '#666', fontSize: 9, position: 'right' }}
                    />
                    <ReferenceLine 
                      y={4} 
                      stroke="#888" 
                      strokeDasharray="3 3"
                      label={{ value: '4 mmol/L (OBLA)', fill: '#888', fontSize: 9, position: 'right' }}
                    />

                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: "rgba(24, 24, 27, 0.95)", 
                        border: "1px solid rgba(255,255,255,0.1)",
                        borderRadius: "8px",
                        color: "#fff"
                      }}
                      formatter={(value) => [`${Number(value).toFixed(2)} mmol/L`, "Laktat"]}
                      labelFormatter={(label) => `${label} Watt`}
                    />
                    
                    <Line 
                      type="monotone" 
                      dataKey="laktat" 
                      stroke="#a855f7" 
                      strokeWidth={3}
                      dot={{ fill: '#a855f7', strokeWidth: 2, r: 5 }}
                      activeDot={{ r: 7, fill: '#a855f7' }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="laktat" 
                      fill="url(#laktatGradient)" 
                      stroke="transparent"
                    />
                    <defs>
                      <linearGradient id="laktatGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#a855f7" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#a855f7" stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              
              {/* Laktat Messwerte Tabelle */}
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2 data-label">{t('analysis.stage')}</th>
                      <th className="text-right py-2 data-label">{t('analysis.power')}</th>
                      <th className="text-right py-2 data-label">{t('analysis.laktat')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analysis.laktat.measurements.map((m, i) => (
                      <tr key={i} className="border-b border-white/5">
                        <td className="py-2">{i + 1}</td>
                        <td className="py-2 text-right font-medium">{m.watt} W</td>
                        <td className="py-2 text-right">
                          <span className="number-display text-purple-400">{m.laktat}</span>
                          <span className="text-xs text-muted-foreground ml-1">mmol/L</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Test Verlauf Chart - Hide for HR-only */}
          {!analysis.is_hr_only && testData.length > 0 && (
            <div className="glass-card rounded-xl p-6 mb-8">
              <h3 className="font-display font-bold uppercase mb-2">
                {analysis.test_type === "20min" ? t('analysis.test20min') : t('analysis.testRamp')} - {t('analysis.progress')}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {t('analysis.progressSubtitle')}
              </p>
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={testData}>
                    <defs>
                      <linearGradient id="powerGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#D4FF00" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#D4FF00" stopOpacity={0.05} />
                      </linearGradient>
                      <linearGradient id="hrGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#FF0055" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#FF0055" stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis 
                      dataKey="timeLabel" 
                      stroke="#666" 
                      tick={{ fontSize: 10 }}
                      interval="preserveStartEnd"
                    />
                    <YAxis 
                      yAxisId="power"
                      stroke="#D4FF00" 
                      domain={[0, maxPower]}
                      tick={{ fontSize: 10 }}
                      label={{ value: 'Watt', angle: -90, position: 'insideLeft', fill: '#D4FF00', fontSize: 12 }}
                    />
                    <YAxis 
                      yAxisId="hr"
                      orientation="right"
                      stroke="#FF0055" 
                      domain={[80, maxHR + 10]}
                      tick={{ fontSize: 10 }}
                      label={{ value: 'BPM', angle: 90, position: 'insideRight', fill: '#FF0055', fontSize: 12 }}
                    />
                    
                    {/* Schwellen-Linien */}
                    <ReferenceLine 
                      yAxisId="power"
                      y={analysis.ftp} 
                      stroke="#D4FF00" 
                      strokeDasharray="5 5"
                      strokeWidth={2}
                      label={{ value: `FTP ${analysis.ftp}W`, fill: '#D4FF00', fontSize: 11, position: 'right' }}
                    />
                    <ReferenceLine 
                      yAxisId="power"
                      y={analysis.vt2} 
                      stroke="#FF0055" 
                      strokeDasharray="5 5"
                      strokeWidth={2}
                      label={{ value: `VT2 ${analysis.vt2}W`, fill: '#FF0055', fontSize: 11, position: 'right' }}
                    />
                    <ReferenceLine 
                      yAxisId="power"
                      y={analysis.vt1} 
                      stroke="#FFAA00" 
                      strokeDasharray="5 5"
                      strokeWidth={2}
                      label={{ value: `VT1 ${analysis.vt1}W`, fill: '#FFAA00', fontSize: 11, position: 'right' }}
                    />
                    
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: "rgba(24, 24, 27, 0.95)", 
                        border: "1px solid rgba(255,255,255,0.1)",
                        borderRadius: "8px"
                      }}
                      formatter={(value, name) => {
                        if (name === "power") return [`${value} W`, t('analysis.power')];
                        if (name === "hr") return [`${value} bpm`, t('analysis.heartRate')];
                        return [value, name];
                      }}
                      labelFormatter={(label) => `${t('analysis.time')}: ${label}`}
                    />
                    <Legend 
                      verticalAlign="top" 
                      height={36}
                      formatter={(value) => {
                        if (value === "power") return `${t('analysis.power')} (W)`;
                        if (value === "hr") return `${t('analysis.heartRate')} (bpm)`;
                        return value;
                      }}
                    />
                    
                    <Area 
                      yAxisId="power"
                      type="monotone" 
                      dataKey="power" 
                      stroke="#D4FF00" 
                      fill="url(#powerGradient)" 
                      strokeWidth={1.5}
                      dot={false}
                      name="power"
                    />
                    <Line 
                      yAxisId="hr"
                      type="monotone" 
                      dataKey="hr" 
                      stroke="#FF0055" 
                      strokeWidth={1.5}
                      dot={false}
                      name="hr"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              
              {/* Legende für Schwellen */}
              <div className="mt-4 flex flex-wrap gap-4 justify-center text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-0.5 bg-volt" style={{ borderTop: '2px dashed #D4FF00' }}></div>
                  <span>FTP ({analysis.ftp}W)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-0.5" style={{ borderTop: '2px dashed #FF0055' }}></div>
                  <span>VT2 ({analysis.vt2}W)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-0.5" style={{ borderTop: '2px dashed #FFAA00' }}></div>
                  <span>VT1 ({analysis.vt1}W)</span>
                </div>
              </div>
            </div>
          )}

          {/* Charts Grid - Hide for HR-only */}
          {!analysis.is_hr_only && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {/* Zone Distribution Chart */}
            <div className="lg:col-span-8 glass-card rounded-xl p-4 sm:p-6">
              <h3 className="font-display font-bold uppercase mb-3 sm:mb-4 text-sm sm:text-base">{t('analysis.powerZonesOverview')}</h3>
              <div className="h-48 sm:h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart 
                    data={zoneData} 
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" horizontal={false} />
                    <XAxis type="number" stroke="#666" domain={[0, 'dataMax']} />
                    <YAxis 
                      type="category" 
                      dataKey="name" 
                      stroke="#666" 
                      tick={{ fontSize: 11 }}
                      width={90}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: "rgba(24, 24, 27, 0.95)", 
                        border: "1px solid rgba(255,255,255,0.1)",
                        borderRadius: "8px",
                        color: "#ffffff"
                      }}
                      itemStyle={{ color: "#ffffff" }}
                      labelStyle={{ color: "#ffffff" }}
                      formatter={(value, name, props) => {
                        const zone = props.payload;
                        return [zone.rangeText, t('analysis.range')];
                      }}
                    />
                    <Bar dataKey="max" radius={[0, 4, 4, 0]}>
                      {zoneData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} fillOpacity={0.8} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            {/* Biometric Profile Info Banner */}
            <div className="lg:col-span-12 glass-card rounded-xl p-4 sm:p-5 border border-volt/20 bg-gradient-to-r from-volt/5 to-transparent">
              <div className="flex items-start sm:items-center gap-3 sm:gap-4">
                <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg bg-volt/20 flex items-center justify-center flex-shrink-0">
                  <Activity className="h-5 w-5 sm:h-6 sm:w-6 text-volt" />
                </div>
                <div>
                  <h4 className="font-display font-bold uppercase text-volt mb-1 text-sm sm:text-base">{t('analysis.biometricProfile')}</h4>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {t('analysis.biometricProfileDesc')}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Peer Comparison - Extended */}
            <div className="lg:col-span-4 glass-card rounded-xl p-4 sm:p-6">
              <h3 className="font-display font-bold uppercase mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
                <Users className="h-4 w-4 sm:h-5 sm:w-5 text-cyan" />
                {t('analysis.peerComparison')}
              </h3>
              <div className="space-y-3 sm:space-y-4">
                {/* Overall Category Badge */}
                <div className="text-center p-3 sm:p-4 bg-zinc-900/50 rounded-lg border border-white/5">
                  <div className={`peer-badge ${getPeerBadgeClass(peerData.category)} text-base sm:text-xl mb-1 sm:mb-2`}>
                    {peerData.category}
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Top {100 - peerData.percentile}% {t('analysis.inYourGroup')}
                  </p>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-2 sm:gap-3">
                  <div className="bg-zinc-900/30 p-3 rounded-lg">
                    <p className="data-label mb-1 text-xs">{t('analysis.ageGroup')}</p>
                    <p className="font-medium">{peerData.age_group} {t('analysis.years')}</p>
                  </div>
                  {peerData.isExtended && (
                    <>
                      <div className="bg-zinc-900/30 p-3 rounded-lg">
                        <p className="data-label mb-1 text-xs">{t('analysis.gender')}</p>
                        <p className="font-medium">{peerData.gender}</p>
                      </div>
                      <div className="bg-zinc-900/30 p-3 rounded-lg">
                        <p className="data-label mb-1 text-xs">{t('analysis.training')}</p>
                        <p className="font-medium">{peerData.training_level}</p>
                      </div>
                    </>
                  )}
                  <div className="bg-zinc-900/30 p-3 rounded-lg">
                    <p className="data-label mb-1 text-xs">VO2max</p>
                    <p className="font-medium text-cyan">{peerData.vo2max}</p>
                  </div>
                </div>

                {/* Metric Comparison Bars */}
                {peerData.isExtended && (
                  <div className="space-y-3 pt-2">
                    <p className="data-label">{t('analysis.metricComparison')}</p>
                    
                    {/* VO2max Bar */}
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span>VO2max</span>
                        <span className="text-cyan">Top {100 - peerData.vo2max_percentile}%</span>
                      </div>
                      <div className="relative h-3 bg-zinc-800 rounded-full overflow-hidden">
                        <div 
                          className="absolute h-full bg-gradient-to-r from-cyan/50 to-cyan rounded-full transition-all duration-500"
                          style={{ width: `${peerData.vo2max_percentile}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* FTP/kg Bar */}
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span>FTP/kg</span>
                        <span className="text-volt">Top {100 - peerData.ftp_percentile}%</span>
                      </div>
                      <div className="relative h-3 bg-zinc-800 rounded-full overflow-hidden">
                        <div 
                          className="absolute h-full bg-gradient-to-r from-volt/50 to-volt rounded-full transition-all duration-500"
                          style={{ width: `${peerData.ftp_percentile}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                )}

                {!peerData.isExtended && (
                  <div>
                    <p className="data-label mb-1">{t('analysis.percentile')}</p>
                    <div className="relative h-4 bg-zinc-800 rounded-full overflow-hidden">
                      <div 
                        className="absolute h-full bg-gradient-to-r from-volt/50 to-volt rounded-full"
                        style={{ width: `${peerData.percentile}%` }}
                      ></div>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Top {100 - peerData.percentile}%
                    </p>
                  </div>
                )}

                {peerData.description && (
                  <p className="text-sm text-muted-foreground border-t border-white/5 pt-4">
                    {peerData.description}
                  </p>
                )}
              </div>
            </div>
          </div>
          )}

          {/* Power Zones & HR Zones */}
          <div className={`grid grid-cols-1 ${analysis.is_hr_only ? '' : 'lg:grid-cols-2'} gap-6 mb-8`}>
            {/* Power Zones - Hide for HR-only */}
            {!analysis.is_hr_only && analysis.power_zones && (
            <div className="glass-card rounded-xl p-6">
              <h3 className="font-display font-bold uppercase mb-4">{t('analysis.powerZones')}</h3>
              <div className="space-y-3">
                {Object.entries(analysis.power_zones).map(([key, zone], index) => (
                  <div key={key} className="flex items-center gap-4">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: ZONE_COLORS[index] }}
                    ></div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium">{zone.name}</span>
                        <span className="text-sm text-muted-foreground">{zone.percent}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="flex-1 h-2 bg-zinc-800 rounded-full mr-4">
                          <div 
                            className="h-full rounded-full"
                            style={{ 
                              width: `${Math.min((zone.max / analysis.ftp) * 50, 100)}%`,
                              backgroundColor: ZONE_COLORS[index]
                            }}
                          ></div>
                        </div>
                        <span className="text-xs text-muted-foreground w-24 text-right">{zone.range} W</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            )}

            {/* Heart Rate Zones */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="font-display font-bold uppercase mb-4 flex items-center gap-2">
                <Heart className="h-5 w-5 text-heart-red" />
                {t('analysis.hrZones')}
              </h3>
              <div className="space-y-3">
                {Object.entries(analysis.heart_rate_zones).map(([key, zone], index) => (
                  <div key={key} className="flex items-center gap-4">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: `hsl(${340 - index * 30}, 80%, 50%)` }}
                    ></div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium">{zone.name}</span>
                        <span className="text-sm text-muted-foreground">{zone.percent}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="flex-1 h-2 bg-zinc-800 rounded-full mr-4">
                          <div 
                            className="h-full rounded-full"
                            style={{ 
                              width: `${(zone.max / analysis.raw_data.max_hr) * 100}%`,
                              backgroundColor: `hsl(${340 - index * 30}, 80%, 50%)`
                            }}
                          ></div>
                        </div>
                        <span className="text-xs text-muted-foreground w-24 text-right">{zone.range} bpm</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Carbohydrate Consumption - Hide for HR-only */}
          {!analysis.is_hr_only && analysis.carb_consumption && (
          <div className="glass-card rounded-xl p-6">
            <h3 className="font-display font-bold uppercase mb-4 flex items-center gap-2">
              <Flame className="h-5 w-5 text-carb-orange" />
              {t('analysis.energyConsumption')}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {t('analysis.energyConsumptionDesc', { weight: Number(analysis.athlete_data?.weight_kg).toFixed(2), ftp: Number(analysis.ftp).toFixed(2) })}
            </p>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={carbData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="watt" stroke="#666" />
                  <YAxis stroke="#666" label={{ value: 'g/h', angle: -90, position: 'insideLeft', fill: '#666' }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: "rgba(24, 24, 27, 0.95)", 
                      border: "1px solid rgba(255,255,255,0.1)",
                      borderRadius: "8px"
                    }}
                    formatter={(value, name, props) => [
                      `${Number(value).toFixed(2)} g/h`, 
                      props.payload.description || t('analysis.carbohydrates')
                    ]}
                  />
                  <Bar dataKey="carbs" fill="#FFAA00" radius={[4, 4, 0, 0]}>
                    {carbData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={`rgba(255, 170, 0, ${0.4 + (index * 0.1)})`} 
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-2 data-label">{t('analysis.power')}</th>
                    <th className="text-left py-2 data-label">{t('analysis.zone')}</th>
                    <th className="text-right py-2 data-label">kcal/h</th>
                    <th className="text-right py-2 data-label">{t('analysis.carbs')}</th>
                    <th className="text-right py-2 data-label">{t('analysis.fat')}</th>
                    <th className="text-right py-2 data-label">{t('analysis.carbPercent')}</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(analysis.carb_consumption)
                    .filter(([watt]) => !watt.startsWith('_'))  // Filter out internal keys like _fatmax
                    .map(([watt, data]) => (
                    <tr key={watt} className="border-b border-white/5">
                      <td className="py-2 font-medium">{watt}</td>
                      <td className="py-2 text-muted-foreground text-xs">{data.description}</td>
                      <td className="py-2 text-right">
                        <span className="number-display text-cyan">{Number(data.kcal_per_hour || 0).toFixed(0)}</span>
                      </td>
                      <td className="py-2 text-right">
                        <span className="number-display text-carb-orange">{Number(data.carbs_g_per_hour).toFixed(0)}</span>
                        <span className="text-xs text-muted-foreground ml-1">g/h</span>
                      </td>
                      <td className="py-2 text-right">
                        <span className="number-display text-green-400">{Number(data.fat_g_per_hour || 0).toFixed(0)}</span>
                        <span className="text-xs text-muted-foreground ml-1">g/h</span>
                      </td>
                      <td className="py-2 text-right text-xs text-muted-foreground">
                        {Number(data.cho_percent || 0).toFixed(0)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          )}

          {/* Athlete History / Progress */}
          {athleteHistory && athleteHistory.history.length > 1 && (
            <div className="glass-card rounded-xl p-6 mt-6">
              <button 
                onClick={() => setShowHistory(!showHistory)}
                className="w-full flex items-center justify-between"
              >
                <h3 className="font-display font-bold uppercase flex items-center gap-2">
                  <History className="h-5 w-5 text-volt" />
                  {t('analysis.historicalProgress')} ({athleteHistory.history.length} Tests)
                </h3>
                {showHistory ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </button>
              
              {showHistory && (
                <div className="mt-6 space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-zinc-900/50 rounded-lg p-4 border border-white/5">
                      <p className="data-label mb-2">{t('analysis.ftpProgress')}</p>
                      <div className="flex items-baseline gap-2">
                        <span className={`text-2xl font-bold ${athleteHistory.summary.ftp_improved ? 'text-green-400' : 'text-red-400'}`}>
                          {athleteHistory.summary.ftp_improved ? '+' : ''}{Number(athleteHistory.summary.ftp_change).toFixed(2)}W
                        </span>
                        <span className={`text-sm ${athleteHistory.summary.ftp_improved ? 'text-green-400' : 'text-red-400'}`}>
                          ({athleteHistory.summary.ftp_improved ? '+' : ''}{Number(athleteHistory.summary.ftp_change_pct).toFixed(2)}%)
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{t('analysis.sinceFirstTest')}</p>
                    </div>
                    
                    <div className="bg-zinc-900/50 rounded-lg p-4 border border-white/5">
                      <p className="data-label mb-2">{t('analysis.vo2maxProgress')}</p>
                      <div className="flex items-baseline gap-2">
                        <span className={`text-2xl font-bold ${athleteHistory.summary.vo2max_improved ? 'text-green-400' : 'text-red-400'}`}>
                          {athleteHistory.summary.vo2max_improved ? '+' : ''}{Number(athleteHistory.summary.vo2max_change).toFixed(2)}
                        </span>
                        <span className={`text-sm ${athleteHistory.summary.vo2max_improved ? 'text-green-400' : 'text-red-400'}`}>
                          ({athleteHistory.summary.vo2max_improved ? '+' : ''}{Number(athleteHistory.summary.vo2max_change_pct).toFixed(2)}%)
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">ml/kg/min</p>
                    </div>
                    
                    <div className="bg-zinc-900/50 rounded-lg p-4 border border-white/5">
                      <p className="data-label mb-2">{t('analysis.wkgProgress')}</p>
                      <div className="flex items-baseline gap-2">
                        <span className={`text-2xl font-bold ${athleteHistory.summary.ftp_per_kg_improved ? 'text-green-400' : 'text-red-400'}`}>
                          {athleteHistory.summary.ftp_per_kg_improved ? '+' : ''}{Number(athleteHistory.summary.ftp_per_kg_change).toFixed(2)}
                        </span>
                        <span className={`text-sm ${athleteHistory.summary.ftp_per_kg_improved ? 'text-green-400' : 'text-red-400'}`}>
                          ({athleteHistory.summary.ftp_per_kg_improved ? '+' : ''}{Number(athleteHistory.summary.ftp_per_kg_change_pct).toFixed(2)}%)
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{t('analysis.wattsPerKg')}</p>
                    </div>
                  </div>

                  {/* Progress Chart */}
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={athleteHistory.history.map(h => ({
                        ...h,
                        dateLabel: formatDateLocal(h.date, { month: "short", year: "2-digit" })
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                        <XAxis dataKey="dateLabel" stroke="#666" />
                        <YAxis yAxisId="ftp" stroke="#D4FF00" label={{ value: 'FTP (W)', angle: -90, position: 'insideLeft', fill: '#D4FF00' }} />
                        <YAxis yAxisId="vo2" orientation="right" stroke="#00F0FF" label={{ value: 'VO2max', angle: 90, position: 'insideRight', fill: '#00F0FF' }} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: "rgba(24, 24, 27, 0.95)", 
                            border: "1px solid rgba(255,255,255,0.1)",
                            borderRadius: "8px"
                          }}
                          formatter={(value, name) => {
                            if (name === 'ftp') return [`${Number(value).toFixed(2)}W`, 'FTP'];
                            if (name === 'vo2max') return [Number(value).toFixed(2), 'VO2max'];
                            if (name === 'ftp_per_kg') return [`${Number(value).toFixed(2)} W/kg`, 'FTP/kg'];
                            return [Number(value).toFixed(2), name];
                          }}
                        />
                        <Legend />
                        <Line 
                          yAxisId="ftp"
                          type="monotone" 
                          dataKey="ftp" 
                          stroke="#D4FF00" 
                          strokeWidth={2}
                          dot={{ fill: '#D4FF00', strokeWidth: 2 }}
                          name="FTP"
                        />
                        <Line 
                          yAxisId="vo2"
                          type="monotone" 
                          dataKey="vo2max" 
                          stroke="#00F0FF" 
                          strokeWidth={2}
                          dot={{ fill: '#00F0FF', strokeWidth: 2 }}
                          name="VO2max"
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>

                  {/* History Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-2 data-label">{t('analysis.date')}</th>
                          <th className="text-left py-2 data-label">{t('analysis.test')}</th>
                          <th className="text-right py-2 data-label">FTP</th>
                          <th className="text-right py-2 data-label">W/kg</th>
                          <th className="text-right py-2 data-label">VO2max</th>
                          <th className="text-right py-2 data-label">{t('analysis.weight')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {athleteHistory.history.slice().reverse().map((test, idx) => (
                          <tr 
                            key={test.analysis_id} 
                            className={`border-b border-white/5 ${test.analysis_id === id ? 'bg-volt/10' : ''}`}
                          >
                            <td className="py-2">
                              {formatDateLocal(test.date)}
                              {test.test_time && (
                                <span className="text-muted-foreground ml-1">{test.test_time}</span>
                              )}
                              {test.analysis_id === id && (
                                <span className="ml-2 text-xs text-volt">({t('analysis.current')})</span>
                              )}
                            </td>
                            <td className="py-2 text-muted-foreground">
                              {test.test_type === "20min" ? "20-Min" : t('analysis.ramp')}
                            </td>
                            <td className="py-2 text-right font-mono text-volt">{Number(test.ftp).toFixed(2)}W</td>
                            <td className="py-2 text-right font-mono">{Number(test.ftp_per_kg).toFixed(2)}</td>
                            <td className="py-2 text-right font-mono text-cyan">{Number(test.vo2max).toFixed(2)}</td>
                            <td className="py-2 text-right text-muted-foreground">{Number(test.weight).toFixed(2)}kg</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Raw Stats */}
          <div className="mt-8 glass-card rounded-xl p-6">
            <h3 className="font-display font-bold uppercase mb-4">{t('analysis.rawData')}</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <p className="data-label">{t('analysis.maxPower')}</p>
                <p className="number-display text-2xl">{analysis.raw_data?.max_power || 0} W</p>
              </div>
              <div>
                <p className="data-label">{t('analysis.avgPower')}</p>
                <p className="number-display text-2xl">{Math.round(analysis.raw_data?.avg_power || 0)} W</p>
              </div>
              <div>
                <p className="data-label">{t('analysis.maxHR')}</p>
                <p className="number-display text-2xl">{analysis.raw_data?.max_hr || 0} bpm</p>
              </div>
              <div>
                <p className="data-label">{t('analysis.duration')}</p>
                <p className="number-display text-2xl">
                  {Math.floor((analysis.raw_data?.duration_seconds || 0) / 60)} min
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Coaching CTA Banner - Only show for non-subscribers */}
        {!hasSubscription && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <div className="glass-card rounded-xl p-8 bg-gradient-to-r from-cyan/10 to-volt/10 border border-cyan/30 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-cyan/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
            <div className="relative flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-center md:text-left">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan/20 border border-cyan/30 mb-3">
                  <TrendingUp className="h-4 w-4 text-cyan" />
                  <span className="text-xs text-cyan font-medium uppercase">{t('analysis.nextStep')}</span>
                </div>
                <h3 className="font-display text-2xl font-bold uppercase mb-2">
                  {i18n.language === 'de' ? 'Aus Daten wird ' : 'Turn Data into '}<span className="text-cyan">{i18n.language === 'de' ? 'Leistung' : 'Performance'}</span>
                </h3>
                <p className="text-muted-foreground max-w-lg">
                  {t('analysis.coachingDesc')}
                </p>
              </div>
              <a 
                href="https://www.loops-cycling-academy.de/coaching-1/#Zu-den-Angeboten" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex-shrink-0"
                data-testid="analysis-coaching-btn"
              >
                <Button className="skew-btn bg-cyan text-black hover:bg-cyan/90 font-bold uppercase tracking-wider px-8 py-4 text-base group whitespace-nowrap">
                  <span>{t('analysis.discoverCoaching')}</span>
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
            </div>
          </div>
        </div>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-md bg-zinc-950 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-red-400">{t('analysis.deleteDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('analysis.deleteDialog.confirm')}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="glass-card rounded-lg p-4 bg-zinc-900/50">
              <p className="font-medium">{analysis?.athlete_name || t('analysis.unnamed')}</p>
              <p className="text-sm text-muted-foreground">
                {analysis?.test_type === "20min" ? t('analysis.test20min') : t('analysis.testRamp')} • 
                {analysis?.created_at && new Date(analysis.created_at).toLocaleDateString(i18n.language === 'de' ? "de-DE" : "en-US")}
              </p>
              <p className="text-volt font-bold mt-2">FTP: {analysis?.ftp}W</p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              className="border-zinc-700"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={deleteAnalysis}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white font-bold"
            >
              {deleting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="sm:max-w-lg bg-zinc-950 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-cyan flex items-center gap-2">
              <Share2 className="h-5 w-5" />
              {t('analysis.shareDialog.title')}
            </DialogTitle>
            <DialogDescription>
              {t('analysis.shareDialog.desc')}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="glass-card rounded-lg p-4 bg-zinc-900/50">
              <p className="text-xs text-muted-foreground mb-2">{t('analysis.shareDialog.linkLabel')}</p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={shareLink}
                  className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm font-mono"
                  data-testid="share-link-input"
                />
                <Button
                  onClick={copyShareLink}
                  className="bg-cyan text-black hover:bg-cyan/90 font-bold"
                  data-testid="copy-link-btn"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  {t('common.copy')}
                </Button>
              </div>
            </div>

            <div className="text-sm text-muted-foreground">
              <p className="flex items-center gap-2">
                <Check className="h-4 w-4 text-volt" />
                {t('analysis.shareDialog.canView')}
              </p>
              <p className="flex items-center gap-2 mt-1">
                <Check className="h-4 w-4 text-volt" />
                {t('analysis.shareDialog.pdfAvailable')}
              </p>
              <p className="flex items-center gap-2 mt-1">
                <Check className="h-4 w-4 text-volt" />
                {t('analysis.shareDialog.noLogin')}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowShareDialog(false)}
              className="border-zinc-700"
            >
              {t('common.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
